import { z } from "zod";
/**
 * UAT (User Acceptance Testing) admin API contracts.
 *
 * UAT grants give named testers temporary access. The admin API manages
 * the grant lifecycle: create, update expiry, list, revoke.
 *
 * All datetimes on responses are ISO 8601 (z.iso.datetime). Dates on
 * requests are date-only YYYY-MM-DD strings, validated for calendar
 * correctness (no 2026-02-30).
 */
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
/**
 * A date-only string in YYYY-MM-DD format that must be a real calendar date.
 * Regex passes format; the refine rejects impossible dates like 2026-02-30.
 */
const dateOnlySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD")
    .refine((s) => {
    // Date.UTC rejects impossible days by rolling over; compare parts to detect it.
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return (dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d);
}, "invalid calendar date");
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
/** `POST /v1/admin/users` — create a new UAT grant. */
export const uatGrantRequestSchema = z.object({
    name: z.string().trim().min(1).max(255),
    email: z.email().max(255),
    expiryDate: dateOnlySchema,
});
/** `PATCH /v1/admin/users/:id/grant` — update expiry on an existing grant. */
export const uatGrantUpdateSchema = z.object({
    expiryDate: dateOnlySchema,
});
// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------
/** Which automation step failed when a grant's error is set. */
export const uatErrorStepSchema = z.enum([
    "account_setup",
    "grant_upsert",
    "email",
    "revoke",
]);
/** A UAT grant row as returned by the admin API. */
export const uatGrantRowSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    /** The Supabase user this grant resolved to, or null if account setup failed. */
    userId: z.uuid().nullable(),
    /** Record creation timestamp (maps to created_at). */
    createdAt: z.iso.datetime(),
    /** Record last-update timestamp (maps to updated_at). */
    updatedAt: z.iso.datetime(),
    /** Access expiry; null until the backend sets it, or after explicit clear. */
    expiresAt: z.iso.datetime().nullable(),
    // Revoke sets expiresAt to now; no separate revokedAt/grantedAt field.
    status: z.enum(["active", "inactive"]),
    /** Which automation step failed; null when no error. Detail belongs in logs. */
    error: uatErrorStepSchema.nullable(),
});
/** `GET /v1/admin/users` response. */
export const uatGrantListSchema = z.array(uatGrantRowSchema);
// ---------------------------------------------------------------------------
// Mutation acknowledgement
// ---------------------------------------------------------------------------
/** Acknowledgement returned by grant/revoke admin API mutations. */
export const uatMutationResultSchema = z.object({ ok: z.literal(true) });
//# sourceMappingURL=uat.js.map